/* eslint-disable import/prefer-default-export */
/* eslint-disable no-array-constructor */
import { updateRecalculateRequiredWidgetHeight } from '../../Lib/redux/slices/dynamicHeight';
import { updateAndExposeDynamicHeightData } from '../../Utils/utils';

/**
 * The following internal function calculates the current height requirements
 * for the most complex and dynamically changing section on the form, the form
 * inputs section.
 */
export const determineRequiredHeightOfFormInputsSection = () => {
  const { inputAreaRef } = window?.__private__?.dynamicWidgetHeightData || {};

  if (inputAreaRef === undefined) {
    return undefined;
  }

  const formInputsElementGroup = inputAreaRef?.current?.children;
  const margin = 19.2; // The MUI component 'div.MuiBox-root.MuiBox-root-3239.makeStyles-box-3237' places a 19.2 px margin above and below each form input and it is equivalent to 1.2 rem
  let formInputsElementGroupHeight = margin; // initial value = top margin
  new Array(...(formInputsElementGroup || [])).forEach((formInputElement) => {
    formInputsElementGroupHeight +=
      formInputElement.getBoundingClientRect().height + margin; // bottom margin of each input element
  });
  const newFormInputsSectionHeight = formInputsElementGroupHeight;
  if (typeof newFormInputsSectionHeight === 'number') {
    return newFormInputsSectionHeight;
  }
  return 0;
};

export const isPageChangeCompleted = ({ pageChangeTracker, currentPage }) => {
  if (
    !pageChangeTracker[currentPage] ||
    !pageChangeTracker[currentPage].length
  ) {
    return false;
  }
  if (
    Array.isArray(pageChangeTracker[currentPage]) &&
    pageChangeTracker[currentPage].length === 3 &&
    pageChangeTracker[currentPage][0] === false &&
    pageChangeTracker[currentPage][1] === true &&
    pageChangeTracker[currentPage][2] === false
  ) {
    return true;
  }
  return false;
};

/**
 * Failsafe logic - Prevent footer from getting pushed off screen
 * Throughout the form flow, some pages require the main form container
 * to be displayed as a flex container (e.g. <Loader />) rather than
 * as a block container. If the transition from 'flex' to 'block'
 * takes too long to complete in the DOM, the height calculations may
 * be misled and set an incorrect height. This would lead to an
 * excessively large input area. To remedy that, this code will
 * re-attempt to correctly calculate the required widget height.
 * This restores the user's ability to initiate navigation.
 */
export const reExamineRequiredWidgetHeight = (
  dispatch,
  isWidgetHeightRecalculationRequired
) => {
  const dynamicInputAreaContainer = document.querySelector(
    '#inputAreaParentContainer'
  );

  if (!dynamicInputAreaContainer) {
    // No need for the failsafe if static height is in play
    return;
  }

  const widget = document.body;
  const widgetHeight = widget?.getBoundingClientRect()?.height;
  const { headerRef, footerRef, failsafeAlgorithmData, isDebugLoggingEnabled } =
    window?.__private__?.dynamicWidgetHeightData || {};

  const headerSectionHeight =
    headerRef?.current?.getBoundingClientRect().height;
  const inputAreaHeight =
    dynamicInputAreaContainer?.getBoundingClientRect().height;
  const outerFooterContainer = footerRef?.current;
  const footerSectionHeight =
    outerFooterContainer?.getBoundingClientRect().height;
  const isFormContainerHeightInvalid =
    typeof headerSectionHeight !== 'number' ||
    Number.isNaN(headerSectionHeight) ||
    typeof inputAreaHeight !== 'number' ||
    Number.isNaN(inputAreaHeight) ||
    typeof footerSectionHeight !== 'number' ||
    Number.isNaN(footerSectionHeight);
  const isFormContainerLargerThanWidgetViewport =
    Math.floor(headerSectionHeight + inputAreaHeight + footerSectionHeight) >
    Math.floor(widgetHeight);
  updateAndExposeDynamicHeightData({
    failsafeAlgorithmData: {
      ...failsafeAlgorithmData,
      timeStampOfLastWidgetHeightReExamination: Date.now(),
    },
  });

  if (isFormContainerHeightInvalid || isFormContainerLargerThanWidgetViewport) {
    if (isDebugLoggingEnabled) {
      // eslint-disable-next-line no-console
      console.log(
        'DH: Failsafe kicks in - invalid form container height gets corrected'
      );
    }
    if (isWidgetHeightRecalculationRequired) {
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: false,
        })
      );
    }
    dispatch(
      updateRecalculateRequiredWidgetHeight({
        isWidgetHeightRecalculationRequired: true,
      })
    );
  }
};

export const getWidgetHeightOverride = (totalHeightOverrides) => {
  if (typeof totalHeightOverrides !== 'object') return undefined;

  const heightOverrides = Object.values(totalHeightOverrides).filter(
    (value) => typeof value === 'number'
  );

  if (heightOverrides.length === 0) return undefined;

  return Math.max(heightOverrides);
};
